import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { FormattedMessage, useIntl } from 'react-intl';
import { BUTTON_VARIANT, H2, P, Accordion } from '@manulife/mux';
import {
  DateOfBirth,
  ErrorAlert,
  Form,
  FormattedRichMessage,
  FormTextInput,
  PASSWORD_INPUT_VARIANTS,
  UsernameInput,
  USERNAME_VARIANTS,
  validEmail,
  ErrorShape,
  REALMS,
  validFirstName,
  validLastName,
  CoBrandingButton,
  PasswordInput,
  TooltipComponent,
  customStyles,
  useRealm,
} from 'ciam-self-service-shared';
import { useDispatch } from 'react-redux';
import LanguageSelection from 'components/common/LanguageSelection';
import useIsInboundSSO from '../../../hooks/useIsInboundSSO';
import FormElement from '../../common/FormElement';
import AnalyticsDataLayer from '../../common/Analytics';
import useDateOfBirthState from '../../../hooks/useDateOfBirthState';
import muxCustomStyles from '../../../assets/styles/muxCustomStyles';
import useGoToUrl from '../../../hooks/useGoToUrl';
import { EVENT_REGISTER_ERROR } from '../constants';
import { registerFailureAction } from '../actions';
import AemNotification from '../../AemNotification';
import InternetExplorerUserWarning from '../../common/InternetExplorerUserWarning';
import usePasswordValidation from '../../../hooks/usePasswordValidation';

dayjs.extend(utc);

function SignupForm(props) {
  const { onSignup, isSigningUp, error } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [dateOfBirthParts, dateOfBirth, setDateOfBirth] = useDateOfBirthState();
  const [preferredLanguage, setPreferredLanguage] = useState(intl?.locale?.slice(0, 2) || 'en');
  const goto = useGoToUrl();
  const isInboundSSO = useIsInboundSSO();
  const inboundButtonStyle = !isInboundSSO ? customStyles.button : muxCustomStyles.inboundButton;
  const realm = useRealm();
  const { passwordErrors, handlePasswordChange: handlePasswordValidation } = usePasswordValidation({
    clearError: () => dispatch(registerFailureAction(null)),
    isSignupFlow: true,
    error,
    firstName,
    lastName,
    username,
  });

  const handleSignup = useCallback(async () => {
    // TODO: Move this to redux. Formatting the date to what the backend requires shouldn't be part of the view.
    const dob = `${dayjs(dateOfBirth).format('YYYY-MM-DD')}T00:00:00Z`;
    const signupData = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      dob,
      email,
      username,
      password,
      goto,
      lang: preferredLanguage,
    };
    onSignup(signupData);
  }, [firstName, lastName, email, username, password, dateOfBirth, goto, preferredLanguage, onSignup]);

  const errorValues = { userName: username };

  const handleUsernameChange = (value) => {
    setUsername(value);
    if (error?.component === 'username') {
      dispatch(registerFailureAction(null));
    }
  };

  const handlePasswordChange = (value) => {
    handlePasswordValidation(value, setPassword);
  };

  const handleEmailChange = useCallback(
    (value) => {
      setEmail(value);
      if (error?.component === 'email') {
        dispatch(registerFailureAction(null));
      }
    },
    [error?.component, dispatch],
  );

  useEffect(() => {
    setPreferredLanguage(intl?.locale?.slice(0, 2) || 'en');
  }, [intl?.locale]);

  useEffect(() => {
    return () => {
      dispatch(registerFailureAction(null));
    };
  }, [dispatch]);

  return (
    <>
      {(!error || Object.keys(error).length !== 0) && (
        <AnalyticsDataLayer eventName={EVENT_REGISTER_ERROR} error={error} ignoreSession />
      )}
      <InternetExplorerUserWarning />
      <P id="instructions">
        <FormattedRichMessage id="signup.instructions" />
      </P>
      <P id="additional-instructions">
        <FormattedRichMessage id={`signup.${realm}.additionalInstructions`} />
      </P>
      <AemNotification displayPage="signUp" />
      <ErrorAlert error={error} errorValues={errorValues} callouts />

      <Form onSubmit={handleSignup} noValidate>
        <FormElement>
          <FormTextInput
            id="first-name"
            name="first-name"
            autoComplete="given-name"
            label={intl.formatMessage({ id: 'signup.personalInfo.firstName' })}
            value={firstName}
            onChange={setFirstName}
            required
            ariaRequired
            validations={validFirstName}
            autoFocus
          />
        </FormElement>

        <FormElement>
          <FormTextInput
            id="last-name"
            name="last-name"
            autoComplete="family-name"
            label={intl.formatMessage({ id: 'signup.personalInfo.lastName' })}
            value={lastName}
            onChange={setLastName}
            required
            ariaRequired
            validations={validLastName}
          />
        </FormElement>

        <FormElement>
          <DateOfBirth
            dateOfBirthParts={dateOfBirthParts}
            dateOfBirth={dateOfBirth}
            onChange={setDateOfBirth}
            required
            ariaRequired
          />
        </FormElement>

        <FormElement>
          <FormTextInput
            id="email"
            inputMode="email"
            autoComplete="email"
            data-testid={intl.formatMessage({ id: 'signup.personalInfo.email' })}
            label={intl.formatMessage({ id: 'signup.personalInfo.email' })}
            value={email}
            onChange={handleEmailChange}
            required
            ariaRequired
            errors={[error]}
            validations={validEmail}
            validationsCombinedLabel={intl.formatMessage({ id: 'common.email.validation.combined' })}
            tooltip={<TooltipComponent contentID="shared.email.tooltip" hideFor={[REALMS.ADVISOR, REALMS.SPONSOR]} />}
          />
        </FormElement>
        <FormElement>
          <LanguageSelection
            preferredLanguage={preferredLanguage}
            onChange={setPreferredLanguage}
            legendName="signup.personalInfo.languageLegend"
          />
        </FormElement>
        <H2>
          <FormattedMessage id="signup.credentials.heading" />
        </H2>
        <FormElement>
          <UsernameInput
            id="username"
            name="username"
            variant={USERNAME_VARIANTS.CREATE}
            value={username}
            onChange={handleUsernameChange}
            errors={[error]}
            errorValues={errorValues}
            required
            ariaRequired
          />
        </FormElement>
        <FormElement>
          <PasswordInput
            id="password"
            variant={PASSWORD_INPUT_VARIANTS.CREATE}
            value={password}
            onChange={handlePasswordChange}
            errors={passwordErrors}
            firstName={firstName}
            lastName={lastName}
            userName={username}
            required
            ariaRequired
          />
        </FormElement>
        {isInboundSSO && (
          <Accordion
            title={intl.formatMessage({
              id: 'signup.personalInfo.termsAndConditions.title',
            })}
            customStyle={{
              titleStyle: { height: 'auto' },
              headerWrapperStyle: { alignItems: 'start' },
            }}
          >
            <FormattedRichMessage id="signup.personalInfo.termsAndConditions.content" />
          </Accordion>
        )}
        <FormElement spacing={2}>
          <CoBrandingButton
            type="submit"
            variant={BUTTON_VARIANT.PRIMARY}
            saving={isSigningUp}
            disabled={isSigningUp}
            customStyle={inboundButtonStyle}
            analyticsData={{
              click: {
                linkName: intl.formatMessage({ id: 'signup.submitButton' }),
                linkHref: '/register/verify',
              },
            }}
          >
            <FormattedMessage id="signup.submitButton" />
          </CoBrandingButton>
        </FormElement>
      </Form>
    </>
  );
}

SignupForm.propTypes = {
  isSigningUp: PropTypes.bool,
  onSignup: PropTypes.func.isRequired,
  error: ErrorShape,
};

SignupForm.defaultProps = {
  isSigningUp: false,
  error: undefined,
};

export default SignupForm;
